import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { AuthContextType } from './AuthTypes'

export const AuthContext = createContext<AuthContextType>({
  authManager: undefined,
  currentUser: undefined,
  isLoading: true,
  error: undefined,
  renderLoginComponent: () => undefined,
})

export function useAuth() {
  return useContext(AuthContext)
}

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthContextType>({
    authManager: undefined,
    currentUser: undefined,
    isLoading: true,
    error: undefined,
    renderLoginComponent: () => undefined,
  })

  const loadAuthVendorProvider = useCallback(async () => {
    if (!state.authManager) {
      switch (import.meta.env.VITE_AUTH_PROVIDER) {
        case 'local': {
          setState({
            ...state,
            authManager: {
              getToken: () =>
                new Promise<string>((resolve) => {
                  if (!import.meta.env.VITE_AUTH_TEST_TOKEN) {
                    throw new Error('VITE_AUTH_TEST_TOKEN is not set')
                  }
                  resolve(import.meta.env.VITE_AUTH_TEST_TOKEN)
                }),
              signOut: () => Promise.resolve(true),

              setError: () => undefined,
            },
            currentUser: {
              email: 'test@test.com',
              displayName: 'Test User',
              emailVerified: true,
            },
            isLoading: false,
            error: undefined,
          })
          break
        }
        case 'auth0': {
          const Auth0 = await import('../../vendors/auth/auth0/Auth0Provider')
          await Auth0.registerAuth0Vendor({ state, setState })
          break
        }
        case 'keycloak': {
          const keycloakModule = await import('../../vendors/auth/keycloak/KeycloakProvider')
          await keycloakModule.registerKeycloakVendor({ state, setState })
          break
        }
        default: {
          throw new Error(`Unsupported auth provider: ${import.meta.env.VITE_AUTH_PROVIDER}`)
        }
      }
    }
  }, [state, setState])

  useEffect(() => {
    void loadAuthVendorProvider()
  }, [loadAuthVendorProvider])

  const contextValue = useMemo(
    () => ({
      authManager: state.authManager,
      currentUser: state.currentUser,
      isLoading: state.isLoading,
      error: state.error,
      renderLoginComponent: state.renderLoginComponent,
    }),
    [state.authManager, state.currentUser, state.isLoading, state.error, state.renderLoginComponent]
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export default AuthProvider
